import React from "react";
import DOMPurify from "dompurify";
import "./DailyQuestionCard.css";

const sanitise = (value) => DOMPurify.sanitize(String(value || ""));

const splitQuestion = (value) => {
  const parts = String(value || "").split(/<br\s*\/?>(?:\s*)/i);
  return {
    primary: sanitise(parts.shift()),
    secondary: sanitise(parts.join("<br />")),
  };
};

const DailyQuestionCardShimmer = () => (
  <article className="daily-question-card daily-question-card--shimmer" aria-hidden="true">
    <span className="daily-question-card__shimmer daily-question-card__shimmer--header" />
    <span className="daily-question-card__shimmer daily-question-card__shimmer--line" />
    <span className="daily-question-card__shimmer daily-question-card__shimmer--line short" />
    <span className="daily-question-card__shimmer daily-question-card__shimmer--footer" />
  </article>
);

const DailyQuestionCard = ({
  onOpenPopup,
  onReview,
  questions = [],
  isLoading,
  isError,
  errorMessage,
  page = 1,
  perPage = 20,
  totalPages = 1,
  onPageChange,
}) => {
  const handleQuestionClick = (question) => {
    if (Number(question.is_question_answered) === 0) {
      onOpenPopup(question);
      return;
    }

    onReview?.(question);
  };

  if (isLoading) {
    return <div className="daily-question-list">{Array.from({ length: 6 }, (_, index) => <DailyQuestionCardShimmer key={index} />)}</div>;
  }

  if (isError) {
    return <div className="daily-question-list__status daily-question-list__status--error">{errorMessage || "Unable to load daily questions."}</div>;
  }

  if (!questions.length) {
    return <div className="daily-question-list__status">No daily questions are available for this language.</div>;
  }

  return (
    <>
      <div className="daily-question-list">
        {questions.map((question, index) => {
          const attempted = Number(question.is_question_answered) === 1;
          const { primary, secondary } = splitQuestion(
            question.question_name_en || question.question_name || question.question_name_hi
          );
          const questionNumber = (Math.max(page, 1) - 1) * perPage + index + 1;

          return (
            <article
              className="daily-question-card"
              key={question.daily_question_id || question.question_id || questionNumber}
              role="button"
              tabIndex={0}
              onClick={() => handleQuestionClick(question)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleQuestionClick(question);
                }
              }}
            >
              <div className="daily-question-card__header">
                <div className="daily-question-card__left">
                  <span className="daily-question-card__number">Q.{String(questionNumber).padStart(2, "0")}</span>
                  <span className={`daily-question-card__status daily-question-card__status--${attempted ? "attempted" : "not-attempted"}`}>
                    {attempted ? "Attempted" : "Not Attempted"}
                  </span>
                </div>
                <span className="daily-question-card__date">{question.publish_date || "-"}</span>
              </div>

              <div className="daily-question-card__question">
                <p dangerouslySetInnerHTML={{ __html: primary }} />
                {secondary && <p className="daily-question-card__hindi" dangerouslySetInnerHTML={{ __html: secondary }} />}
              </div>

              <div className="daily-question-card__divider" />
              <div className={`daily-question-card__footer daily-question-card__footer--${attempted ? "attempted" : "not-attempted"}`}>
                <span className="daily-question-card__info"><i className={`bi ${attempted ? "bi-eye" : "bi-info-lg"}`} /></span>
                <span>{attempted ? "Tap to review your answer" : "This question was not attempted that day"}</span>
                {attempted && <i className="bi bi-chevron-right daily-question-card__arrow" />}
              </div>
            </article>
          );
        })}
      </div>

      {totalPages > 1 && (
        <nav className="daily-question-pagination" aria-label="Daily question pages">
          <button type="button" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>Next</button>
        </nav>
      )}
    </>
  );
};

export default DailyQuestionCard;
